import type { SelectOption } from '@web/components/select';

import { useAbortController } from '@packages/core/http';
import {
    type FailCodeEntity,
    type FailCodePartEntity,
    type FailCodeSectionEntity,
    type FailCodeSubsectionEntity,
    FetchSubsections,
    LookupFailCodes,
    ResolveSectionAndSubsection,
    SearchPartNumbers,
} from '@packages/domain/fail-codes';
import { container } from '@web/services/container';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { FailCodeSelection, FailCodeVehicleParams } from '../fail-codes-modal.types';

/**
 * Parameters accepted by the {@link useSearchByPart} hook.
 */
export type UseSearchByPartParams = {
    /** Whether to prefill the first part number result on mount (used by "View matching fail codes"). */
    prefillPartNumber?: boolean;

    /** Vehicle identification params used to scope every fail code and part number lookup. */
    vehicleParams: FailCodeVehicleParams;
};

// Operations are prefixed with $ to make them visually distinct in this file.
const [$fetchSubsections, $lookupFailCodes, $resolveSectionAndSubsection, $searchPartNumbers] =
    container.resolveOperations(FetchSubsections, LookupFailCodes, ResolveSectionAndSubsection, SearchPartNumbers);

/**
 * Manages the multi-step "search by part" fail code flow.
 *
 * The flow is: search parts -> select part -> look up fail codes for that part -> if only one
 * fail code, auto-select it -> resolve section/subsection (auto-select when unambiguous). This
 * hook owns all state for the cascade, maps results to select options for the UI, and handles
 * abort-controller cleanup on unmount.
 *
 * @param params - vehicle params and optional prefill flag
 * @returns hook state and handlers for the search-by-part fail code flow
 */
export function useSearchByPart({ prefillPartNumber, vehicleParams }: UseSearchByPartParams) {
    // State
    const [confirmedCodeSelection, setConfirmedCodeSelection] = useState(false);
    const [didCheckSections, setDidCheckSections] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isOnlyMatch, setIsOnlyMatch] = useState(false);
    const [partFailCodeData, setPartFailCodeData] = useState<FailCodeEntity[]>([]);
    const [partOptionData, setPartOptionData] = useState<FailCodePartEntity[]>([]);
    const [selectedFailCodeSection, setSelectedFailCodeSection] = useState<FailCodeSectionEntity>();
    const [selectedFailCodeSubsection, setSelectedFailCodeSubsection] = useState<FailCodeSubsectionEntity>();
    const [selectedPartFailCode, setSelectedPartFailCode] = useState<FailCodeEntity>();
    const [selectedPartOption, setSelectedPartOption] = useState<FailCodePartEntity>();
    const [sectionOptionData, setSectionOptionData] = useState<SelectOption[]>([]);
    const [subsectionOptionData, setSubsectionOptionData] = useState<SelectOption[]>([]);

    // Hooks
    const { abortSignalRef, signalAbort } = useAbortController();
    const searchInputRef = useRef<{
        /** Programmatically hide the typeahead dropdown menu. */
        hideMenu: () => void;
        /** Programmatically toggle the typeahead dropdown menu. */
        toggleMenu: () => void;
    } | null>(null);

    // Derived state
    const autoSelectedSectionName = useMemo(() => {
        if (sectionOptionData.length === 2) {
            return (JSON.parse(sectionOptionData[1].value) as FailCodeSectionEntity).sectionName;
        }
        return undefined;
    }, [sectionOptionData]);

    const autoSelectedSubsectionName = useMemo(() => {
        if (sectionOptionData.length === 2 && subsectionOptionData.length === 2) {
            return (JSON.parse(subsectionOptionData[1].value) as FailCodeSubsectionEntity).subsectionName;
        }
        return undefined;
    }, [sectionOptionData, subsectionOptionData]);

    /** Whether the user must manually pick section/subsection (they were not auto-selected). */
    const showSectionSelects = didCheckSections && (!autoSelectedSectionName || !autoSelectedSubsectionName);

    /** Whether the "Add Fail Code" button should be disabled due to missing section/subsection. */
    const isAddDisabled =
        showSectionSelects && (!selectedFailCodeSection?.sectionName || !selectedFailCodeSubsection?.subsectionName);

    /** Whether the "Select Fail Code" button should be disabled (no fail code chosen yet). */
    const isSelectDisabled = !selectedPartFailCode;

    // Handlers

    /**
     * Search for part numbers matching the user's query text. Signals any in-flight request
     * before issuing a new one to avoid stale results.
     *
     * @param query - the text typed by the user
     */
    const handlePartSearch = useCallback(
        (query: string) => {
            setIsLoading(true);
            signalAbort();

            $searchPartNumbers({
                modelCode: vehicleParams.modelCode,
                modelYear: vehicleParams.modelYear,
                optionCode: vehicleParams.optionCode,
                query,
                signal: abortSignalRef.current,
            })
                .then((result) => {
                    setIsLoading(false);

                    // Update part options and toggle the dropdown if needed
                    setPartOptionData(result.parts);
                    const el = document.getElementById('fail-code-search');
                    if (!el) {
                        searchInputRef.current?.toggleMenu();
                    }
                })
                .catch(() => {
                    setIsLoading(false);
                });
        },
        [abortSignalRef, signalAbort, vehicleParams.modelCode, vehicleParams.modelYear, vehicleParams.optionCode]
    );

    /**
     * Handle the user selecting a part from the typeahead dropdown. Clears downstream state
     * (fail code selection, confirmation) to reset the cascade.
     *
     * @param part - the selected part, or undefined if cleared
     */
    const handlePartSelect = useCallback((part: FailCodePartEntity | undefined) => {
        setSelectedPartOption(part);
        setSelectedPartFailCode(undefined);
        setConfirmedCodeSelection(false);
        setIsOnlyMatch(false);
    }, []);

    /**
     * Handle the user selecting a fail code from the radio list (when multiple fail codes
     * matched the part).
     *
     * @param failCode - the selected fail code
     */
    const handleFailCodeSelect = useCallback((failCode: FailCodeEntity) => {
        setSelectedPartFailCode(failCode);
    }, []);

    /**
     * Handle the user changing the section dropdown. Clears the subsection selection since
     * it depends on the chosen section.
     *
     * @param value - the JSON-serialized section value from the select, or empty string to clear
     */
    const handleSectionChange = useCallback((value: string) => {
        if (!value) {
            setSelectedFailCodeSection(undefined);
            return;
        }
        setSelectedFailCodeSection(JSON.parse(value) as FailCodeSectionEntity);
    }, []);

    /**
     * Handle the user changing the subsection dropdown.
     *
     * @param value - the JSON-serialized subsection value from the select, or empty string to clear
     */
    const handleSubsectionChange = useCallback((value: string) => {
        if (!value) {
            setSelectedFailCodeSubsection(undefined);
            return;
        }
        setSelectedFailCodeSubsection(JSON.parse(value) as FailCodeSubsectionEntity);
    }, []);

    /**
     * Handle the user pressing the typeahead input (hides the menu so results refresh on next search).
     */
    const handleInputChange = useCallback(() => {
        searchInputRef.current?.hideMenu();
    }, []);

    /**
     * Handle the "Select Fail Code" button press. Transitions from the fail code picker to the
     * section/subsection confirmation step.
     */
    const handleConfirmFailCodeSelection = useCallback(() => {
        setConfirmedCodeSelection(true);
        setSelectedFailCodeSection(undefined);
        setSelectedFailCodeSubsection(undefined);
    }, []);

    /**
     * Handle the "Back" button press. If the user is on the confirmation step, returns to the
     * fail code picker step. Returns `true` if handled internally (stayed within the component),
     * `false` if the caller should navigate back to the search method selector.
     *
     * @returns whether the back action was handled internally
     */
    const handleBack = useCallback((): boolean => {
        if (confirmedCodeSelection) {
            setConfirmedCodeSelection(false);
            return true;
        }
        return false;
    }, [confirmedCodeSelection]);

    /**
     * Build and return the final fail code selection. Should only be called when the user
     * confirms on the section/subsection step.
     *
     * @returns the fail code selection to persist on the case, or undefined if prerequisites are missing
     */
    function confirmSelection(): FailCodeSelection | undefined {
        if (!selectedPartOption || !selectedPartFailCode) {
            return undefined;
        }

        return {
            failCode: selectedPartFailCode.failCode,
            repairCode: selectedPartFailCode.failCode,
            section: selectedFailCodeSection?.sectionName,
            subSection: selectedFailCodeSubsection?.subsectionName,
        };
    }

    // Effects

    // Prefill part number on mount when "View matching fail codes" is used
    useEffect(() => {
        if (!prefillPartNumber) {
            return;
        }

        /** Fetch parts and auto-select the first result for the prefill flow. */
        async function fetchPrefillData() {
            try {
                setIsLoading(true);

                const result = await $searchPartNumbers({
                    modelCode: vehicleParams.modelCode,
                    modelYear: vehicleParams.modelYear,
                    optionCode: vehicleParams.optionCode,
                    query: '',
                    signal: abortSignalRef.current,
                });

                // Auto-select the first part and populate the options list
                setSelectedPartOption(result.parts[0]);
                setPartOptionData(result.parts);
            } catch (error) {
                window.alert((error as Error).message);
            } finally {
                setIsLoading(false);
            }
        }

        fetchPrefillData();
    }, [abortSignalRef, prefillPartNumber, vehicleParams.modelCode, vehicleParams.modelYear, vehicleParams.optionCode]);

    // Look up fail codes when a part is selected
    useEffect(() => {
        if (!selectedPartOption?.partNumber) {
            return;
        }

        $lookupFailCodes({
            modelCode: vehicleParams.modelCode,
            modelYear: vehicleParams.modelYear,
            optionCode: vehicleParams.optionCode,
            partNumber: selectedPartOption.partNumber,
        }).then((codes) => {
            // Auto-select and confirm if exactly one fail code matched
            if (codes.length === 1) {
                setSelectedPartFailCode(codes[0]);
                setConfirmedCodeSelection(true);
                setIsOnlyMatch(true);
            }

            setPartFailCodeData(codes);
        });
    }, [selectedPartOption, vehicleParams.modelCode, vehicleParams.modelYear, vehicleParams.optionCode]);

    // Resolve sections and subsections when a fail code is confirmed
    useEffect(() => {
        if (!selectedPartFailCode || !confirmedCodeSelection) {
            return;
        }

        setDidCheckSections(false);

        $resolveSectionAndSubsection({
            failCode: selectedPartFailCode.failCode,
            modelCode: vehicleParams.modelCode,
            modelYear: vehicleParams.modelYear,
            optionCode: vehicleParams.optionCode,
            signal: abortSignalRef.current,
        })
            .then((result) => {
                // Build section select options with a placeholder
                const sectionOptions: SelectOption[] = result.sectionData.map((section) => ({
                    title: section.sectionName,
                    value: JSON.stringify(section),
                }));
                setSectionOptionData([{ title: 'Select section', value: '' }, ...sectionOptions]);

                // Auto-select section if resolved
                if (result.autoSelectedSection) {
                    setSelectedFailCodeSection(result.autoSelectedSection);
                }

                // Build subsection select options if available
                if (result.subsectionData) {
                    const subsectionOptions: SelectOption[] = result.subsectionData.map((subsection) => ({
                        title: subsection.subsectionName,
                        value: JSON.stringify(subsection),
                    }));
                    setSubsectionOptionData([{ title: 'Select subsection', value: '' }, ...subsectionOptions]);
                }

                // Auto-select subsection if resolved
                if (result.autoSelectedSubsection) {
                    setSelectedFailCodeSubsection(result.autoSelectedSubsection);
                }
            })
            .finally(() => {
                setDidCheckSections(true);
            });
    }, [
        abortSignalRef,
        confirmedCodeSelection,
        selectedPartFailCode,
        vehicleParams.modelCode,
        vehicleParams.modelYear,
        vehicleParams.optionCode,
    ]);

    // Fetch subsections when the user manually selects a section
    useEffect(() => {
        if (!selectedPartFailCode || !selectedFailCodeSection) {
            return;
        }

        // Skip if the section was auto-resolved; subsections were already fetched
        // by the ResolveSectionAndSubsection effect above
        if (autoSelectedSectionName) {
            return;
        }

        $fetchSubsections({
            failCode: selectedPartFailCode.failCode,
            modelCode: vehicleParams.modelCode,
            modelYear: vehicleParams.modelYear,
            optionCode: vehicleParams.optionCode,
            sectionName: selectedFailCodeSection.sectionName,
            sectionNumber: selectedFailCodeSection.sectionNumber,
            signal: abortSignalRef.current,
        }).then((subsections) => {
            const subsectionOptions: SelectOption[] = subsections.map((subsection) => ({
                title: subsection.subsectionName,
                value: JSON.stringify(subsection),
            }));

            // Auto-select subsection if exactly one was returned
            if (subsectionOptions.length === 1) {
                setSelectedFailCodeSubsection(JSON.parse(subsectionOptions[0].value) as FailCodeSubsectionEntity);
            }

            setSubsectionOptionData([{ title: 'Select subsection', value: '' }, ...subsectionOptions]);
        });
    }, [
        abortSignalRef,
        autoSelectedSectionName,
        selectedFailCodeSection,
        selectedPartFailCode,
        vehicleParams.modelCode,
        vehicleParams.modelYear,
        vehicleParams.optionCode,
    ]);

    return {
        /** The auto-resolved section name, or undefined if multiple sections exist. */
        autoSelectedSectionName,

        /** The auto-resolved subsection name, or undefined if multiple subsections exist. */
        autoSelectedSubsectionName,

        /**
         * Build and return the final fail code selection. Returns undefined if prerequisites
         * are missing (no part or fail code selected).
         */
        confirmSelection,

        /** Whether the user has confirmed their fail code choice and is on the section/subsection step. */
        confirmedCodeSelection,

        /**
         * Handle the "Back" button. Returns true if handled internally (went back a step),
         * false if the caller should navigate to the search method selector.
         */
        handleBack,

        /** Handle the "Select Fail Code" button to move to the confirmation step. */
        handleConfirmFailCodeSelection,

        /** Handle a fail code radio button selection (when multiple fail codes match the part). */
        handleFailCodeSelect,

        /** Handle the typeahead input text changing (hides dropdown for fresh search). */
        handleInputChange,

        /** Handle search queries from the typeahead. */
        handlePartSearch,

        /** Handle selecting a part from the typeahead dropdown. */
        handlePartSelect,

        /** Handle the section dropdown value changing. */
        handleSectionChange,

        /** Handle the subsection dropdown value changing. */
        handleSubsectionChange,

        /** Whether the "Add Fail Code" button should be disabled. */
        isAddDisabled,

        /** Whether a part search request is currently in flight. */
        isLoading,

        /** Whether the matched fail code was the only one (triggers the "single match" message). */
        isOnlyMatch,

        /** Whether the "Select Fail Code" button should be disabled. */
        isSelectDisabled,

        /** Fail codes matching the selected part. */
        partFailCodeData,

        /** Part options returned from the most recent search. */
        partOptionData,

        /** Ref to pass to the AsyncTypeahead for programmatic menu control. */
        searchInputRef,

        /** Section dropdown options (including placeholder). */
        sectionOptionData,

        /** The currently selected fail code section, if any. */
        selectedFailCodeSection,

        /** The currently selected fail code subsection, if any. */
        selectedFailCodeSubsection,

        /** The currently selected fail code from the radio list. */
        selectedPartFailCode,

        /** The currently selected part from the typeahead. */
        selectedPartOption,

        /** Whether the section/subsection selects should be visible. */
        showSectionSelects,

        /** Subsection dropdown options (including placeholder). */
        subsectionOptionData,
    };
}
